function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import React, { useCallback, useMemo } from 'react';
import { createContext, isFunction } from '../../utils';
import { useLocale } from '../utils';
import { usePP2Theme } from './use-pp2-theme';
var [InternalThemedValueProvider, useInternalThemedValueSwitch] = createContext({
  name: 'ThemedValueProvider',
  errorMessage: 'useThemedValueSwitch: Seems you forgot to wrap the components in `<ThemedValueProvider />`'
});

/**
 * Returns a function to switch values depending on the current site.
 *
 * `values` is a record of values keyed by theme. You can also include
 * a `default` value in this object, but for conciseness there is a second
 * argument to the function, `defaultValue`, which will be used if
 * there is no `default` in the values object.
 *
 * The returned value is the one with the current theme as the key,
 * or the default value if no value has been passed which matches
 * the current theme. If no default value was provided, `undefined`
 * is returned.
 *
 * e.g.
 * ```
 * const color = tvs({wales: 'yellow'}, 'blue');
 * ```
 *
 * If the value is a function it will be called and the result returned.
 * This is particularly useful if returning different components for
 * each theme, as it avoids rendering the components which aren't needed.
 *
 * e.g.
 * ```
 * <div>
 *   tvs({
 *     wales: () => <WalesHeader/>,
 *     ni: () => <NiHeader/>,
 *     default: () => <Header/>,
 *   })
 * </div>
 * ```
 *
 * For Northern Ireland you can use `"northern-ireland"` (which will
 * need to be quoted), or just `ni`. If both are included, the values
 * for 'northern-ireland' will be used.
 *
 * The TypeScript type of this function has various overloads to try
 * to make it work in most situations. If you find a place where the
 * type doesn't work, you can specify a generic type, e.g.
 * ```
 * tvs<string | Record[] | null>({...})
 * ```
 *
 * The name of the function, `tvs`, is short for 'themed value switch'.
 * Function names should generally be unabbreviated and descriptive,
 * but in this case brevity is more important as this is a utility
 * function which will be used all over the place.
 *
 * We also return boolean values for `isEngland`, `isWales` and `isNI`.
 */
export var useThemedValueSwitch = useInternalThemedValueSwitch;

/**
 * Provide the theme value switching function and booleans.
 *
 * Rather than creating a new tvs() function each time
 * useThemedValueSwitch is used, instead create a single function
 * and share it between all uses.
 *
 * This is included in the PP2ThemeProvider, so you shouldn't need
 * to use it on its own.
 */
export var ThemedValueProvider = _ref => {
  var {
    children
  } = _ref;
  var locale = useLocale();
  var theme = usePP2Theme();
  var tvs = useCallback(function (values, defaultValue) {
    var keys = Object.keys(values);
    var value = defaultValue;
    if (keys.includes(locale)) {
      value = values[locale];
    } else if (locale === 'northern-ireland' && keys.includes('ni')) {
      value = values['ni'];
    } else if (keys.includes('default')) {
      value = values['default'];
    }
    if (isFunction(value)) {
      return value(theme);
    }
    return value;
  }, [locale, theme]);
  var isThemes = useMemo(() => {
    return {
      isEngland: locale === 'england',
      isWales: locale === 'wales',
      isNI: locale === 'northern-ireland',
      isTQ: locale === 'tq'
    };
  }, [locale]);
  var themedValueSwitch = _objectSpread({
    tvs
  }, isThemes);
  return /*#__PURE__*/React.createElement(InternalThemedValueProvider, {
    value: themedValueSwitch
  }, children);
};